<!--
  ~ This code is protected by intellectual property rights.
  ~ Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
  ~ © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
  -->

<!-- START ~~ LOADING STATE, ONLY LOADER VISIBLE -->
@if (isLoading()) {
  @if (announceLoading()) {
    <span class="profile:sr-only" role="status" aria-live="polite" aria-atomic="true">
      {{ translations.general.loadingStatus }}
    </span>
  }
  <pidx-skeleton-loader></pidx-skeleton-loader>
} @else {
  <!-- START ~~ EMPTY STATE, NO EMAIL ADDRESSES AVAILABLE -->
  @if (!allEmails().length) {
    <div class="profile:flex profile:flex-col profile:md:flex-row">
      <div class="profile:w-full profile:md:w-1/4">
        <profile-pds-p-text p-text size="small" color="contrast-medium">
          <div [innerHTML]="translations.general.primaryEmailLabel"></div>
        </profile-pds-p-text>
      </div>
      <div class="profile:w-full profile:md:w-1/2 profile:mt-fluid-md profile:md:mt-0">
        <profile-pds-p-text p-text size="small" color="contrast-medium">
          <div [innerHTML]="translations.general.noAddressesAvailable"></div>
        </profile-pds-p-text>
      </div>
    </div>
  }
  <!-- END ~~ EMPTY STATE -->
  <!-- START ~~ PRIMARY EMAIL & EDITOR -->
  @if (primaryEmail()) {
    <div class="profile:flex profile:flex-col profile:md:flex-row">
      <div class="profile:w-full profile:md:w-1/4">
        <profile-pds-p-text id="addNewEmailEditor" p-text size="small" color="contrast-medium">
          <div [innerHTML]="translations.general.primaryEmailLabel"></div>
        </profile-pds-p-text>
      </div>
      <div class="profile:w-full profile:md:w-3/4 profile:mt-fluid-md profile:md:mt-0">
        @if (CallingContainerMode.PRIMARY === activeCallingContainerMode()) {
          <div>
            @if (errorKey()) {
              <pidx-inline-form-messages [messages]="[errorKey() | translate]"></pidx-inline-form-messages>
            }
          </div>
        }
        @if (showList([primaryEmail()!], emailObjectBeingEdited())) {
          <myprofile-email-list
            [emails]="[primaryEmail()!]"
            [emailAddressBeingEdited]="emailObjectBeingEdited()"
            [totalEmailCount]="allEmails().length"
            [verifiedReplacementAvailable]="hasVerifiedReplacement()"
            (enterEditModeEvent)="enterEditMode($event, CallingContainerMode.PRIMARY)"
            (deleteItemEvent)="deleteEmailAddress($event, CallingContainerMode.PRIMARY)"
            (goToChangePorscheIdPageEvent)="goToPorscheIdChangePage()"
            [translations]="translations.list"
          ></myprofile-email-list>
        }
        <div class="profile:mt-fluid-md profile:md:mt-0">
          @if (showEditorForEmails([primaryEmail()])) {
            @if (emailBeingVerified()) {
              <myprofile-email-verification
                [translations]="translations.verification"
                [email]="emailBeingVerified()!"
                [country]="country"
                [locale]="locale"
                (verified)="onEmailVerified()"
                (cancelEvent)="cancelVerification(emailBeingVerified()!)"
                (reloadEvent)="onVerificationReload()"
              ></myprofile-email-verification>
            } @else {
              <myprofile-email-editor
                [translations]="translations.editor"
                [email]="emailObjectBeingEdited()!"
                [emailAlreadyExistsError]="emailAlreadyExistsError()"
                (leaveEditModeEvent)="leaveEditMode()"
                (updateEmailAddressEvent)="updateExistingEmailAddress($event, CallingContainerMode.PRIMARY)"
                (goToChangePorscheIdPageEvent)="goToPorscheIdChangePage()"
              ></myprofile-email-editor>
            }
          }
        </div>
      </div>
    </div>
  }
  <!-- END ~~ PRIMARY EMAIL & EDITOR -->
  <!-- START ~~ ADDITIONAL EMAILS & EDITOR -->
  @if (additionalEmails().length) {
    <div class="profile:mt-fluid-md profile:flex profile:flex-col profile:md:flex-row">
      <div class="profile:w-full profile:md:w-1/4">
        <profile-pds-p-text p-text size="small" color="contrast-medium">
          <div [innerHTML]="translations.general.additionalEmailsLabel"></div>
        </profile-pds-p-text>
      </div>
      <div class="profile:w-full profile:md:w-3/4 profile:mt-fluid-md profile:md:mt-0">
        @if (CallingContainerMode.ADDITIONAL === activeCallingContainerMode()) {
          <div>
            @if (errorKey()) {
              <pidx-inline-form-messages [messages]="[errorKey() | translate]"></pidx-inline-form-messages>
            }
          </div>
        }
        @if (showList(additionalEmails(), emailObjectBeingEdited())) {
          <myprofile-email-list
            [emails]="additionalEmails()"
            [emailAddressBeingEdited]="emailObjectBeingEdited()"
            [translations]="translations.list"
            [isCnUser]="isCnUser"
            [totalEmailCount]="allEmails().length"
            [restrictGlobalUIChanges]="restrictGlobalUIChanges"
            (enterEditModeEvent)="enterEditMode($event, CallingContainerMode.ADDITIONAL)"
            (deleteItemEvent)="deleteEmailAddress($event, CallingContainerMode.ADDITIONAL)"
            (goToChangePorscheIdPageEvent)="goToPorscheIdChangePage()"
          ></myprofile-email-list>
        }
        <div class="profile:mt-fluid-md profile:md:mt-0">
          @if (showEditorForEmails(additionalEmails())) {
            @if (emailBeingVerified()) {
              <myprofile-email-verification
                [translations]="translations.verification"
                [email]="emailBeingVerified()!"
                [country]="country"
                [locale]="locale"
                (verified)="onEmailVerified()"
                (cancelEvent)="cancelVerification(emailBeingVerified()!)"
                (reloadEvent)="onVerificationReload()"
              ></myprofile-email-verification>
            } @else {
              <myprofile-email-editor
                [translations]="translations.editor"
                [email]="emailObjectBeingEdited()!"
                [emailAlreadyExistsError]="emailAlreadyExistsError()"
                (leaveEditModeEvent)="leaveEditMode()"
                (updateEmailAddressEvent)="updateExistingEmailAddress($event, CallingContainerMode.ADDITIONAL)"
                (goToChangePorscheIdPageEvent)="goToPorscheIdChangePage()"
              ></myprofile-email-editor>
            }
          }
        </div>
      </div>
    </div>
  }
  <!-- END ~~ ADDITIONAL EMAILS & EDITOR-->
  <!-- START ~~ NEW EMAIL EDITOR -->
  @if (shouldShowEditor() && !emailObjectBeingEdited()) {
    <div class="profile:flex profile:flex-col profile:md:flex-row">
      <div class="profile:w-full profile:md:w-1/4">
        <profile-pds-p-text p-text size="small" color="contrast-medium">
          <div id="addNewEmailLabel" [innerHTML]="translations.general.addNewEmail"></div>
        </profile-pds-p-text>
      </div>
      <div class="profile:w-full profile:md:w-3/4 profile:mt-fluid-md profile:md:mt-0">
        @if (activeCallingContainerMode() === CallingContainerMode.ADD_EMAIL) {
          <div>
            @if (errorKey()) {
              <pidx-inline-form-messages [messages]="[errorKey() | translate]"></pidx-inline-form-messages>
            }
          </div>
        }
        <div class="profile:mt-fluid-md profile:md:mt-0">
          @if (emailBeingVerified()) {
            <myprofile-email-verification
              [translations]="translations.verification"
              [email]="emailBeingVerified()!"
              [country]="country"
              [locale]="locale"
              (verified)="onEmailVerified()"
              (cancelEvent)="cancelVerification(emailBeingVerified()!)"
              (reloadEvent)="onVerificationReload()"
            ></myprofile-email-verification>
          } @else {
            <myprofile-email-editor
              [translations]="translations.editor"
              [isFirstEmail]="!allEmails().length"
              [emailAlreadyExistsError]="emailAlreadyExistsError()"
              (leaveEditModeEvent)="leaveEditMode()"
              (updateEmailAddressEvent)="addNewEmailAddress($event, CallingContainerMode.ADD_EMAIL)"
            ></myprofile-email-editor>
          }
        </div>
      </div>
    </div>
  }
  <!-- END ~~ EMAIL EDITOR -->
  <!-- START ~~ ADD NEW EMAIL BUTTON -->
  @if (!shouldShowEditor() && allEmails().length < MAX_EMAILS) {
    @if (!isCnUser || (isCnUser && !restrictGlobalUIChanges)) {
      <div class="profile:flex profile:flex-col profile:md:flex-row">
        <div class="profile:w-full profile:md:w-1/4"></div>
        <div>
          <profile-pds-p-button
            class="profile:mt-fluid-md"
            p-button
            (click)="enterEditMode(null, CallingContainerMode.ADD_EMAIL)"
            variant="secondary"
            icon="plus"
            data-protractor-id="addEmailAddressButton"
            id="addEmailAddressButton"
            [myprofileUiTrack]="{
              clickElementType: 'button',
              clickElementId: 'email_address',
              clickElementName: 'add',
            }"
            >{{ translations.general.addButtonLabel }}
          </profile-pds-p-button>
        </div>
      </div>
    }
  }
  <!-- END ~~ ADD NEW EMAIL BUTTON -->
  <!-- START ~~ MESSAGE TOO MANY EMAILS -->
  @if (!shouldShowEditor() && allEmails().length >= MAX_EMAILS) {
    <profile-pds-p-text p-text size="small">
      <div [innerHTML]="translations.general.tooManyAddressesLabel"></div>
    </profile-pds-p-text>
  }
  <!-- END ~~ MESSAGE TOO MANY EMAILS -->
  <!-- START ~~ OVERLAY LOADER, CONTENT VISIBLE AS WELL AS LOADER OVERLAY -->
  @if (isRefreshing()) {
    <pidx-loader [mode]="PidxLoaderModeEnum.OVERLAY" [text]="translations.general.savingOverlay"></pidx-loader>
  }
  <!-- END ~~ OVERLAY LOADER -->
}
<!-- END ~~ LOADING STATE -->
